/**
 * EditorialBadge — small-caps tone variants (positive / warning /
 * negative / neutral / accent) replacing the old saturated pills.
 *
 * Editorial reference:
 *   - `.verified` (positive): mono, uppercase, var(--go) ink, vector check —
 *     NOT a green pill.
 *   - `.tag.g/.a/.r` (risk cells): mono, uppercase, status ink + a
 *     low-alpha status border — NOT a filled chip.
 *
 * Theme-aware: ink + border read roleColor/roleBorder(theme, <tone>).
 * `variant='check'` prepends a vector check (for "Verified" citations);
 * `variant='outline'` draws the low-alpha border (for risk/verdict
 * cells). Default `variant='text'` is the bare small-caps inline tag.
 */
import React from 'react';
import { Check } from 'lucide-react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor, roleBorder, type ReportColorRole } from '../../../../lib/reportRoleColors';

const MONO = 'var(--report-body-font, inherit)';

export type EditorialBadgeTone = 'positive' | 'warning' | 'negative' | 'neutral' | 'accent';
export type EditorialBadgeVariant = 'text' | 'outline' | 'check';

export interface EditorialBadgeProps {
  theme: ReportTheme;
  tone: EditorialBadgeTone;
  children: React.ReactNode;
  variant?: EditorialBadgeVariant;
  className?: string;
  style?: React.CSSProperties;
  /** ARIA label override (defaults to the text content via children). */
  title?: string;
  /**
   * Allow the badge label to wrap onto multiple lines (e.g. in a narrow
   * table cell). Default `false` → `whiteSpace: 'nowrap'` (existing
   * behaviour, preserved for all current callers).
   */
  wrap?: boolean;
}

const INK_ROLE: Record<EditorialBadgeTone, ReportColorRole> = {
  positive: 'positive',
  warning: 'warning',
  negative: 'negative',
  neutral: 'muted',
  accent: 'accent',
};

export function EditorialBadge({
  theme,
  tone,
  children,
  variant = 'text',
  className,
  style,
  title,
  wrap = false,
}: EditorialBadgeProps): React.ReactElement {
  const inkRole = INK_ROLE[tone];
  const ink = roleColor(theme, inkRole);
  const isOutline = variant === 'outline';

  return (
    <span
      className={className}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: MONO,
        fontSize: isOutline ? 9 : 9.5,
        fontWeight: 600,
        letterSpacing: isOutline ? '0.06em' : '0.1em',
        textTransform: 'uppercase',
        color: ink,
        border: isOutline ? `1px solid ${roleBorder(theme, inkRole)}` : undefined,
        padding: isOutline ? '2px 7px' : undefined,
        whiteSpace: wrap ? 'normal' : 'nowrap',
        ...style,
      }}
    >
      {variant === 'check' && (
        <Check
          aria-hidden="true"
          data-pdf-badge-check="true"
          style={{ width: '0.9em', height: '0.9em' }}
          strokeWidth={2}
        />
      )}
      {children}
    </span>
  );
}
